/**
 * Tonnie Languages Academy — the QultureUp World that opens Comuniverse's
 * language courses (docs/TONNIE-COMMUNICATION-PROFICIENCY-ASSESSMENT.md, R4).
 * What the citizen's endpoints answer (`/api/heroes/qultureup/academy`).
 */
import type { ExternalWorldProgress } from './external-worlds';

/** The languages the Academy teaches, as Tonnie names them, in the order the World offers them. */
export const LANGUAGE_ACADEMY_LANGUAGES = ['en', 'pt-PT', 'es', 'it'] as const;
export type LanguageAcademyLanguage = (typeof LANGUAGE_ACADEMY_LANGUAGES)[number];

/** A CEFR level the Academy is told — never A0 (the platform holds no such result). */
export type LanguageAcademyLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

/** What the citizen's consent lets Tonnie send the school — sent whenever it exists. */
export const LANGUAGE_ACADEMY_SHARED_FIELDS = [
  'NAME',
  'EMAIL',
  'LEARNING_LANGUAGE',
  'LEVEL',
  'UI_LANGUAGE',
  'BIRTH_DATE',
  'PHONE',
  'NATIONALITY',
] as const;
export type LanguageAcademySharedField = (typeof LANGUAGE_ACADEMY_SHARED_FIELDS)[number];

/**
 * Why the school cannot be reached from this environment: it names no school
 * address (`NOT_CONFIGURED`), or the school's data agreement is not signed and
 * this is production (`PARTNER_PENDING`). The `LANGUAGE_ACADEMY_UNAVAILABLE`
 * refusal carries one of these as `details.reason`.
 */
export const LANGUAGE_ACADEMY_PARTNER_UNAVAILABLE_REASONS = [
  'NOT_CONFIGURED',
  'PARTNER_PENDING',
] as const;
export type LanguageAcademyPartnerUnavailableReason =
  (typeof LANGUAGE_ACADEMY_PARTNER_UNAVAILABLE_REASONS)[number];

/**
 * Why the Academy cannot be opened for this person now: the journey's QultureUp
 * assessment is not done (`ASSESSMENT_REQUIRED` — the Academy is QultureUp's
 * third World, and it opens only after the assessment; owner decision 23 Oct
 * 2026), or the school cannot be reached from here (a partner reason). The
 * person's own step comes first: it is said before an environment's.
 */
export const LANGUAGE_ACADEMY_UNAVAILABLE_REASONS = [
  'ASSESSMENT_REQUIRED',
  ...LANGUAGE_ACADEMY_PARTNER_UNAVAILABLE_REASONS,
] as const;
export type LanguageAcademyUnavailableReason =
  (typeof LANGUAGE_ACADEMY_UNAVAILABLE_REASONS)[number];

/** The Academy as the citizen meets it. Every value is the server's; the screen renders it. */
export interface LanguageAcademyState {
  available: boolean;
  unavailableReason: LanguageAcademyUnavailableReason | null;
  /**
   * The journey this state was read for — the one the request named, else the
   * person's primary — whose QultureUp assessment opens the Academy; null when
   * the person has no journey (then the reason is `ASSESSMENT_REQUIRED`).
   */
  journeyId: string | null;
  /**
   * The school's public demonstration site (`COMUNIVERSE_DEMO_URL`) — a plain
   * link: no code, nothing of the person shared. Local and staging only;
   * production refuses to boot with it set, so there it is always null.
   */
  demoUrl: string | null;
  /** The school, as the partner registry names it for citizens. */
  partner: {
    code: string;
    displayName: string;
    websiteUrl: string;
    logoUrl: string | null;
  };
  /**
   * The languages the citizen may open the Academy in: all four before the
   * first opening; after it, only the one it fixed (`learner.courseLanguage`).
   */
  languages: LanguageAcademyLanguage[];
  consent: {
    /** The version of the consent text the citizen is asked to accept now. */
    currentVersion: string;
    /** The fields that consent shares. */
    scope: LanguageAcademySharedField[];
    /** The standing consent, if any. */
    given: { id: string; textVersion: string; grantedAt: string } | null;
    /** The standing consent is to the current text — the Academy opens. */
    isCurrent: boolean;
  };
  /** The citizen's place at the school — null until the first opening. */
  learner: {
    /**
     * The language of the course — FIXED by the first opening (D1: the school
     * reads it only on the first visit); an opening in another is refused
     * (`LANGUAGE_ACADEMY_LANGUAGE_FIXED`).
     */
    courseLanguage: LanguageAcademyLanguage;
    lastLaunchedAt: string;
  } | null;
  /** The level the school would be told per language — a valid TestIt result's CEFR. */
  suggestedLevels: Partial<Record<LanguageAcademyLanguage, LanguageAcademyLevel>>;
  /**
   * What the citizen has done at the school, as the World shows it (D6, D8) —
   * computed by the server from what the school pushed; null until the first
   * opening (there is no place at the school to report on).
   */
  progress: ExternalWorldProgress | null;
}

/** An opening: the school's address with a one-time code, to open in a new tab at once. */
export interface LanguageAcademyEntry {
  entryUrl: string;
  /** When the code in it stops working (seconds away). */
  expiresAt: string;
}
